import { useSession } from '@batiflow/client-core/react';
import { UserAvatar, type UserAvatarProps } from '@batiflow/ui';
import {
  useOwnPresence,
  useOwnStatusMessage,
  usePresenceMessageOf,
  usePresenceOf,
} from './presence';

/**
 * A person's avatar with their presence dot from the realtime store (13-ui-ux.md: UserAvatar with
 * presence). The dot, and its label in the accessible name, appear only while realtime is
 * connected; no connection means unknown, never "offline".
 *
 * The server's snapshot lists the person's contacts, never the person: their own avatar (their
 * messages, comments…) shows their own status, the one of the account avatar, instead of the
 * "offline" given to everyone the snapshot does not list.
 */
export function PersonAvatar({
  userId,
  ...props
}: Omit<UserAvatarProps, 'presence'> & { userId: string }) {
  const currentUserId = useSession((state) => state.me?.user.id ?? null);
  const own = useOwnPresence().presence;
  const other = usePresenceOf(userId);
  const ownMessage = useOwnStatusMessage().message;
  const otherMessage = usePresenceMessageOf(userId);
  const isOwn = userId === currentUserId;
  const presence = isOwn ? own : other;
  // A person who is offline has no message (the server clears it).
  const statusMessage = isOwn ? ownMessage : otherMessage;
  return (
    <UserAvatar
      {...props}
      {...(presence ? { presence } : {})}
      {...(statusMessage && presence !== 'offline' ? { statusMessage } : {})}
    />
  );
}
